// ─── Multi-tenant configuration ───────────────────────────────────────────────
// Each tenant maps to a subdomain on opscom.co.uk.
//   ockham.opscom.co.uk  → Platinum customer (Ockham Rescue)
//   demo.opscom.co.uk    → Sales/demo environment
//   medirek.opscom.co.uk → Gold customer (Medirek)
//
// The subdomain is detected in src/proxy.ts and stored in a cookie
// (tenant-id). Client components read this via useTenant().
// ─────────────────────────────────────────────────────────────────────────────

export type TenantTier = "basic" | "silver" | "gold" | "platinum";

/**
 * Platform operating mode.
 *
 * demo        — All features use mock/seeded data. Safe for sales, development,
 *               and training. No real patient or operational data.
 *
 * pilot_live  — Real Supabase-backed data for enabled modules.
 *               Modules not yet integrated show professional empty states.
 *               Partially enabled for a live operational pilot.
 *
 * production  — Full live platform. All enabled modules use real data.
 *               Zero mock data rendered anywhere.
 */
export type PlatformMode = "demo" | "pilot_live" | "production";

/**
 * Module keys. Used to control which modules are active in pilot_live /
 * production mode. In demo mode all modules are always active.
 */
export type ModuleKey =
  | "accounts"
  | "permissions"
  | "staff_records"
  | "onboarding"
  | "applications"
  | "cad"
  | "command_room"
  | "timeline"
  | "prfs"
  | "notifications"
  | "audit_log"
  | "exports"
  | "shifts"
  | "fleet"
  | "governance"
  | "compliance"
  | "training"
  | "my_pay"
  | "reports"
  | "logistics"
  | "cqc"
  | "safeguarding"
  | "payroll"
  | "calendar";

export interface TenantConfig {
  id: string;
  orgName: string;
  shortName: string;
  /** Short uppercase abbreviation for auto-generated reference numbers (e.g. "OCK") */
  abbreviation: string;
  tier: TenantTier;
  /** Platform operating mode — controls mock vs. live data rendering */
  platformMode: PlatformMode;
  /**
   * Modules active in this tenant. Only consulted in pilot_live / production
   * mode. In demo mode all modules are always shown. Modules not listed are
   * either hidden or shown as "integration pending".
   */
  enabledModules: ModuleKey[];
  /** Tailwind gradient classes for the logo square */
  accentFrom: string;
  accentTo: string;
  /** Shadow class for the logo square */
  accentShadow: string;
  tagline: string;
  /** Whether to show the "quick login as…" demo shortcuts on the login page */
  showDemoShortcuts: boolean;
  /** Path to the logo image in /public. If null, falls back to gradient icon. */
  logo: string | null;
}

export const TENANTS: Record<string, TenantConfig> = {
  ockham: {
    id: "ockham",
    orgName: "Ockham Rescue",
    shortName: "Ockham Rescue",
    abbreviation: "OCK",
    tier: "platinum",
    platformMode: "production",
    enabledModules: [
      "accounts",
      "permissions",
      "staff_records",
      "onboarding",
      "applications",
      "cad",
      "command_room",
      "timeline",
      "prfs",
      "notifications",
      "audit_log",
      "exports",
      "shifts",
      "fleet",
      "calendar",
    ],
    accentFrom: "from-blue-500",
    accentTo: "to-blue-700",
    accentShadow: "shadow-blue-900/50",
    tagline: "Combined Ambulance & Fire Co-Response",
    showDemoShortcuts: false,
    logo: "/ockhamlogo.jpeg",
  },
  demo: {
    id: "demo",
    orgName: "OpsCom Demo",
    shortName: "OpsCom Demo",
    abbreviation: "OPS",
    tier: "platinum",
    platformMode: "demo",
    enabledModules: [], // irrelevant in demo mode — all modules shown
    accentFrom: "from-indigo-500",
    accentTo: "to-indigo-700",
    accentShadow: "shadow-indigo-900/50",
    tagline: "Demo Environment — All features unlocked",
    showDemoShortcuts: true,
    logo: "/opscom.png",
  },
  medirek: {
    id: "medirek",
    orgName: "Medirek",
    shortName: "Medirek",
    abbreviation: "MDR",
    tier: "gold",
    platformMode: "demo",
    enabledModules: [], // not yet in pilot — runs demo mode until live
    accentFrom: "from-emerald-500",
    accentTo: "to-emerald-700",
    accentShadow: "shadow-emerald-900/50",
    tagline: "Clinical Management Platform",
    showDemoShortcuts: false,
    logo: null,
  },
};

export const DEFAULT_TENANT = TENANTS.demo;

/** Read the tenant id from a cookie string (server or client) */
export function parseTenantId(cookieString: string): string {
  const match = cookieString.match(/(?:^|;\s*)tenant-id=([^;]+)/);
  return match?.[1] ?? "demo";
}

/** Lookup a tenant config by id, falling back to demo */
export function getTenant(id: string): TenantConfig {
  return TENANTS[id] ?? DEFAULT_TENANT;
}

/** True when the tenant is operating on real live data (not mock/demo) */
export function isLiveTenant(tenant: TenantConfig): boolean {
  return tenant.platformMode === "pilot_live" || tenant.platformMode === "production";
}

/** True when a specific module is operationally active for this tenant */
export function isModuleEnabled(tenant: TenantConfig, module: ModuleKey): boolean {
  if (tenant.platformMode === "demo") return true;
  return tenant.enabledModules.includes(module);
}

